import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowUpRight, ArrowDown, Sparkle } from "@phosphor-icons/react";
import { Flask, Dna, Stethoscope, ChartLineUp, Heartbeat, Devices, UserFocus, Brain, Pulse, HandCoins } from "@phosphor-icons/react";
import { MaskedLine, Overline, FadeUp } from "../components/Reveal";
import { CompanyTeasers } from "../components/CompanyTeasers";
import { ServiceMarquee } from "../components/Marquee";
import { SERVICES, PRODUCTS, PLATFORMS } from "../data/services";
import { AUDIENCES } from "../data/audiences";

const Hl = ({ children, dark = false }) => (
  <span className={`font-semibold underline decoration-accent decoration-2 underline-offset-4 ${dark ? "text-white" : "text-primary"}`}>{children}</span>
);


const ICONS = { Flask, Dna, Stethoscope, ChartLineUp, Heartbeat, Devices, UserFocus, Brain, Pulse, HandCoins };
const SPANS = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-6"];

const CHAPTERS = [
  {
    num: "01",
    title: "Precision",
    text: "Medicine is becoming a data science. Our AI turns biological signal into clinical certainty — pipelines that are validated, reproducible, and fast enough for the bedside.",
    punch: "Validated, reproducible, fast enough for the bedside.",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1200&q=80",
  },
  {
    num: "02",
    title: "Intelligence",
    text: "Healthcare drowns in data and uses too little. Our AI closes the gap — predictive models and decision support woven into the workflows clinicians already trust, without the cost of conventional IT.",
    punch: "Without the cost of conventional IT.",
    image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1200&q=80",
  },
  {
    num: "03",
    title: "Abundance",
    text: "We build to serve, not to squeeze. Enormous profit isn't the goal — lower costs, higher quality, and value shared with patients and partners is.",
    punch: "We build to serve, not to squeeze.",
    image: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    num: "04",
    title: "Humanity",
    text: "Technology only matters if patients feel it. We design every touchpoint to be humane, personalised, and continuously improved — measuring quality of life as rigorously as outcomes.",
    punch: "Quality of life, measured as rigorously as outcomes.",
    image: "/assets/patient-experience.jpeg",
  },
  {
    num: "05",
    title: "Velocity",
    text: "The old guard is slow by design — vast, layered vendors who bolt AI onto legacy systems and move like elephants. We're built the opposite way: AI-native, lean, and fast. No armies of billable teams, no year-long rollouts. We ship at the speed care actually needs.",
    image: "/assets/elephant-ai.png",
  },
];

const STATS = [
  { value: "AI-native", label: "every product and service, by design" },
  { value: "30 days", label: "from your challenge to our delivery" },
  { value: "4", label: "live products — Eaglei, PatExcite, Clinixify, My Kind" },
  { value: "24/7", label: "AI-powered support and updates" },
];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const chipY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 lg:pt-40 pb-16 lg:pb-24" data-testid="home-hero">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute top-64 -left-40 h-[28rem] w-[28rem] rounded-full bg-brand-200/40 blur-3xl" />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="flex items-center gap-2 text-sm sm:text-base font-bold tracking-[0.2em] uppercase text-accent" data-testid="hero-overline">
                <Sparkle size={16} weight="fill" /> Anxya AI OS & PaaS — Universal AI for Every Industry
              </p>
            </FadeUp>
            <h1 className="mt-8 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-primary leading-[0.95]">
              <MaskedLine delay={0.15}>One platform.</MaskedLine>
              <MaskedLine delay={0.3} className="text-primary/50">Every industry.</MaskedLine>
              <MaskedLine delay={0.45} className="italic font-normal">Every jurisdiction.</MaskedLine>
            </h1>
            <FadeUp delay={0.65} className="mt-8 max-w-xl">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Anxya Tech is building a universal AI operating system and PaaS for organizations, governments and nonprofits — with Anxya Health as the first vertical implementation. Discover, build, run, clone and deploy agents, workflows, software and compliant intelligence with human governance built in.
              </p>
            </FadeUp>
            <FadeUp delay={0.8} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/universal-ai-os"
                data-testid="hero-explore-services-btn"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                Anxya All-Industry Platform <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link
                to="/demo"
                data-testid="hero-book-demo-btn"
                className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent hover:shadow-lg hover:shadow-accent/10"
              >
                Talk to Us
              </Link>
            </FadeUp>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="overflow-hidden rounded-t-full border border-primary/10 aspect-[3/4] max-w-sm mx-auto">
                <motion.img
                  style={{ y: imgY }}
                  src="/assets/geneiux-product.png"
                  alt="Anxya AI OS and PaaS platform interface"
                  className="h-full w-full object-cover scale-110"
                />
              </div>
              <motion.div
                style={{ y: chipY }}
                className="absolute -left-4 bottom-10 sm:-left-10 rounded-2xl border border-primary/10 bg-white/90 backdrop-blur-md px-5 py-4 shadow-xl shadow-primary/5"
                data-testid="hero-stat-chip"
              >
                <p className="font-display text-2xl font-semibold text-primary">Universal AI OS</p>
                <p className="text-xs text-muted-foreground">agents, workflows, software, compliance and marketplaces across every industry</p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <FadeUp delay={1} className="mt-16 hidden lg:flex justify-center">
          <ArrowDown className="text-primary/30 animate-bounce" size={20} />
        </FadeUp>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="py-24 lg:py-32" data-testid="manifesto-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <Overline>Our Manifesto</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary max-w-2xl">
          Five convictions behind everything we build
        </h2>
      </FadeUp>
      <div className="mt-16 space-y-12 lg:space-y-16">
        {CHAPTERS.map((c, i) => (
          <div key={c.num} className="grid gap-8 lg:grid-cols-12 items-center">
            <div className="lg:col-span-3">
              <span className="lg:sticky lg:top-32 block font-display text-7xl lg:text-8xl font-light text-primary/10 select-none leading-none">
                {c.num}
              </span>
            </div>
            <div className="lg:col-span-5">
              <FadeUp>
                <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-foreground">{c.title}</h3>
                <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">{c.text}</p>
                <p className="mt-4 text-base sm:text-lg"><Hl>{c.punch}</Hl></p>
              </FadeUp>
            </div>
            <div className="lg:col-span-4">
              <FadeUp delay={0.15}>
                <div className={`overflow-hidden border border-primary/10 ${i % 2 ? "rounded-3xl" : "rounded-t-full"} aspect-[4/3] max-w-sm ${i % 2 ? "lg:ml-auto" : ""}`}>
                  <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                </div>
              </FadeUp>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ServicesBento = () => (
  <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="services-bento">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>What We Do</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
            Nine disciplines. One continuum of care.
          </h2>
        </div>
        <Link to="/services" data-testid="bento-view-all-link" className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors">
          View all services <ArrowUpRight size={16} weight="bold" />
        </Link>
      </FadeUp>
      <div className="mt-14 grid gap-4 md:grid-cols-6">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          const wide = SPANS[i] === "md:col-span-4";
          return (
            <FadeUp key={s.slug} delay={i * 0.05} className={SPANS[i]}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group flex h-full flex-col justify-between rounded-2xl border border-primary/10 bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-xl hover:shadow-accent/10"
              >
                <div className="flex items-start justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/5 text-primary transition-colors duration-300 group-hover:bg-accent group-hover:text-primary">
                    <Icon size={24} weight="duotone" />
                  </span>
                  <ArrowUpRight size={20} className="text-primary/20 transition-all duration-300 group-hover:text-accent group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <div className="mt-10">
                  <h3 className="font-display text-xl font-medium tracking-tight text-foreground">{s.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
                  {wide && <p className="mt-4 text-sm text-muted-foreground/80 leading-relaxed max-w-md hidden md:block">{s.description}</p>}
                </div>
              </Link>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const ProductBand = ({ slug, flip = false, first = false }) => {
  const platform = PLATFORMS.find((p) => p.slug === slug);
  const product = PRODUCTS.find((p) => p.slug === slug) || platform;
  return (
    <section className={`${first ? "pt-20 lg:pt-24 pb-10" : "py-10"}`} data-testid={`product-band-${slug}`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="grid overflow-hidden rounded-3xl border border-primary/10 bg-white lg:grid-cols-12 lg:items-stretch">
            <div className={`lg:col-span-6 bg-muted/40 p-6 lg:p-10 flex items-center ${flip ? "lg:order-2" : ""}`}>
              <div className="overflow-hidden rounded-2xl border border-primary/10 shadow-lg shadow-primary/5 w-full">
                {product.video ? (
                  <video
                    poster={product.image}
                    autoPlay
                    muted
                    loop
                    playsInline
                    data-testid={`product-band-video-${slug}`}
                    className="w-full object-cover"
                  >
                    <source src={product.video.replace(".mp4", ".webm")} type="video/webm" />
                    <source src={product.video} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={product.image}
                    alt={`${product.name} — ${product.tagline}`}
                    className="w-full object-cover transition-transform duration-700 hover:scale-[1.02]"
                    loading="lazy"
                  />
                )}
              </div>
            </div>
            <div className={`lg:col-span-6 p-10 lg:p-14 flex flex-col justify-center ${flip ? "lg:order-1" : ""}`}>
              <Overline className="flex items-center gap-2">{platform ? "Our Platform" : "Our Product"} <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-[10px] font-bold tracking-wider text-accent">{product.soon ? "SOON" : "LIVE"}</span></Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
                {product.name} — {product.bandTitle || product.tagline}
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                {product.bandText || product.description}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={`/products/${product.slug}`}
                  data-testid={`product-band-explore-${slug}`}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                >
                  Explore {product.name} <ArrowUpRight size={16} weight="bold" />
                </Link>
                {product.external ? (
                  <a
                    href={product.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-band-visit-${slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                  >
                    Visit {product.website.replace("https://", "")} <ArrowUpRight size={16} weight="bold" />
                  </a>
                ) : (
                  <Link
                    to={`/products/${slug}#enquire`}
                    data-testid={`product-band-visit-${slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                  >
                    Enquire <ArrowUpRight size={16} weight="bold" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const ValueProp = () => (
  <section className="py-24 lg:py-32 bg-primary text-white" data-testid="value-prop">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <Overline>Why build with us</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight max-w-3xl">
          Legacy vendors sell AI coating. We build AI-native.
        </h2>
        <p className="mt-6 max-w-3xl text-base sm:text-lg text-white/70 leading-relaxed">
          It's time to move past <Hl dark>old-gen, conventional</Hl> products, solutions and services companies — slow, layered, and bolted-on. Everything we build is <Hl dark>AI from the ground up</Hl>, and you feel the difference everywhere:
        </p>
      </FadeUp>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          "Agile by default",
          "Faster time to market",
          "Less downtime, more availability",
          "A fraction of the cost",
          "Higher accuracy & quality",
          "Compliance built in",
          "Better user experience",
          "Fewer hassles, faster support",
        ].map((b, i) => (
          <FadeUp key={b} delay={i * 0.05}>
            <div className="flex h-full items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-5 py-4" data-testid={`value-chip-${i}`}>
              <span className="h-2 w-2 rounded-full bg-accent shrink-0" />
              <p className="text-sm font-semibold text-white">{b}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2}>
        <div className="mt-12 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-accent/40 bg-accent/10 p-8" data-testid="offer-rare-disease">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">Rare disease programmes</p>
            <p className="mt-3 text-base text-white/85 leading-relaxed">
              Almost free, with credits. Patients waiting on the edges shouldn't be priced out by enterprise vendors — we refuse to play that game.
            </p>
          </div>
          <div className="rounded-2xl border border-accent/40 bg-accent/10 p-8" data-testid="offer-pre-revenue">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">Pre-revenue companies</p>
            <p className="mt-3 text-base text-white/85 leading-relaxed">
              Not in market, not generating revenue yet? Special discounts and free credits — we ride with you to Go-to-Market at bare-minimum cost.
            </p>
          </div>
        </div>
      </FadeUp>
      <FadeUp delay={0.3}>
        <p className="mt-10 max-w-3xl text-sm text-white/50 leading-relaxed">
          Profit was never our measure. Our founder lives from abundance, not scarcity — and this company is built to <Hl dark>improve patient experience and care</Hl>, and to raise our <Hl dark>collective health index</Hl>. That's not CSR. It's our DNA.
        </p>
      </FadeUp>
    </div>
  </section>
);

const ChallengeBox = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, service: "30-Day Product Challenge" });
      toast.success("Challenge received — we'll be in touch within one business day.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 lg:py-32" data-testid="challenge-box">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <FadeUp>
          <div className="grid gap-10 lg:grid-cols-2 items-center rounded-3xl border border-accent/40 bg-white p-10 lg:p-14 shadow-xl shadow-accent/5">
            <div>
              <Overline>The 30-Day Challenge</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
                Tell us your requirement — be it a product, solution, or challenge.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Our resolve is to solve it in days, not months or years. AI-native means we don't need quarters — we need weeks.
              </p>
            </div>
            <form onSubmit={submit} data-testid="challenge-form" className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="challenge-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className="h-12 w-full rounded-md border border-primary/10 bg-muted/50 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
                <input
                  data-testid="challenge-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Work email"
                  className="h-12 w-full rounded-md border border-primary/10 bg-muted/50 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
              </div>
              <textarea
                data-testid="challenge-message-input"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Describe your product requirement or challenge…"
                className="w-full rounded-md border border-primary/10 bg-muted/50 px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              />
              <button
                type="submit"
                data-testid="challenge-submit-btn"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
              >
                {submitting ? "Sending…" : "Challenge Us"} <ArrowUpRight size={16} weight="bold" />
              </button>
            </form>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const UniversalPlatformTeaser = () => (
  <section className="py-20 lg:py-24 bg-white border-y border-primary/10" data-testid="universal-platform-teaser">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <div className="grid gap-8 rounded-3xl border border-primary/10 bg-background p-8 lg:p-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-8">
            <Overline>Universal Anxya</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
              Anxya AI OS & PaaS — one platform for every industry, function and jurisdiction.
            </h2>
            <p className="mt-4 max-w-3xl text-sm sm:text-base text-muted-foreground leading-relaxed">
              A universal intelligence layer for agents, workflows, software, assessments, consulting, compliance and marketplaces. Anxya Health is the first vertical implementation.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/universal-ai-os"
              data-testid="home-universal-platform-btn"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
            >
              Anxya All-Industry Platform <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

const StatsBand = () => (
  <section className="py-24 lg:py-32 bg-primary text-white" data-testid="stats-band">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <Overline>Proof, not promises</Overline>
      </FadeUp>
      <div className="mt-12 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.1}>
            <p className="font-display text-5xl lg:text-6xl font-light tracking-tighter text-white">
              {s.value}
            </p>
            <p className="mt-3 text-sm text-white/60">{s.label}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const CTABand = () => (
  <section className="py-24 lg:py-32" data-testid="home-cta">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <div className="rounded-3xl border border-primary/10 bg-white p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div className="max-w-xl">
            <Overline>Start the conversation</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
              Ready to make your data clinically useful?
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              Tell us where your organisation is on the journey — we'll show you what the next step looks like.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/demo"
              data-testid="cta-book-demo-btn"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
            >
              Talk to Us <ArrowUpRight size={16} weight="bold" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);

export const AudiencesStrip = () => (
  <section className="pb-16 lg:pb-20" data-testid="audiences-strip">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <FadeUp>
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-muted-foreground text-center">Who we serve</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {AUDIENCES.map((a) => (
            <Link
              key={a.slug}
              to={`/audiences/${a.slug}`}
              data-testid={`audience-${a.slug}`}
              className="rounded-full border border-primary/15 bg-white px-5 py-2.5 text-sm font-medium text-primary transition-all duration-300 hover:border-accent hover:-translate-y-0.5"
            >
              {a.name}
            </Link>
          ))}
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <AudiencesStrip />
      <ServiceMarquee />
      <Manifesto />
      <ValueProp />
      <CompanyTeasers />
      <ChallengeBox />
      <ServicesBento />
      <UniversalPlatformTeaser />
      <ProductBand slug="geneiux" first />
      <ProductBand slug="eaglei" flip />
      <ProductBand slug="patexcite" />
      <ProductBand slug="clinixify" flip />
      <ProductBand slug="mastercxm" />
      <ProductBand slug="mykind" flip />
      <StatsBand />
      <CTABand />
    </>
  );
}
